import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
  Phone,
  Mail,
  User,
  BarChart2,
  TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { emailTemplatesAPI, inadimplenciaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatCPF = (cpf) => {
  if (!cpf) return '';
  const s = String(cpf).replace(/\D/g, '').padStart(11, '0');
  if (s.length !== 11) return cpf;
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
};

const atrasoColor = (dias) => {
  if (dias <= 30) return '#27ae60';
  if (dias <= 60) return '#f39c12';
  if (dias <= 90) return '#e67e22';
  return '#e74c3c';
};

const faixaConfig = [
  { key: '1-30',  label: '1–30 dias',  color: '#27ae60' },
  { key: '31-60', label: '31–60 dias', color: '#f39c12' },
  { key: '61-90', label: '61–90 dias', color: '#e67e22' },
  { key: '+90',   label: '+90 dias',   color: '#e74c3c' },
];

const faixasAnalise = [
  { key: '1-30',   label: '1–30 d',   color: '#27ae60' },
  { key: '31-60',  label: '31–60 d',  color: '#f39c12' },
  { key: '61-90',  label: '61–90 d',  color: '#e67e22' },
  { key: '90-120', label: '90–120 d', color: '#e74c3c' },
  { key: '120+',   label: '+120 d',   color: '#8e1a0e' },
];

const tiposAnalise = [
  { key: 'mensalidade',    label: 'Mensalidade',    color: '#1565c0' },
  { key: 'coparticipacao', label: 'Coparticipação', color: '#e65100' },
  { key: 'fap',            label: 'FAP',            color: '#4a148c' },
  { key: 'outros',         label: 'Outros',         color: '#555' },
];

// ---------------------------------------------------------------------------
// Sparkline SVG simples para tendência
// ---------------------------------------------------------------------------
const Sparkline = ({ dados, height = 60, color = 'var(--brand, #20a490)' }) => {
  if (!dados || dados.length < 2) return null;
  const vals = dados.map((d) => d.valor);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 300;
  const h = height;
  const pad = 4;
  const pts = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {pts.map((pt, i) => {
        const [x, y] = pt.split(',').map(Number);
        return <circle key={i} cx={x} cy={y} r={3} fill={color} />;
      })}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Score bar visual
// ---------------------------------------------------------------------------
const ScoreBar = ({ score }) => {
  const color = score >= 100 ? '#e74c3c' : score >= 70 ? '#e74c3c' : score >= 40 ? '#e67e22' : '#27ae60';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        flex: 1,
        height: 8,
        background: '#f0f0f0',
        borderRadius: 4,
        overflow: 'hidden',
        minWidth: 60,
      }}>
        <div style={{
          width: `${Math.min(100, score)}%`,
          height: '100%',
          background: color,
          borderRadius: 4,
          transition: 'width 0.3s',
        }} />
      </div>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: score >= 100 ? '#e74c3c' : color, minWidth: 30, textAlign: 'right' }}>
        {Math.round(score)}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Badge de tipo de dívida
// ---------------------------------------------------------------------------
const tipoBadgeStyle = (desc = '') => {
  const d = desc.toUpperCase();
  if (d.includes('MENSALIDADE') || d.includes('TAXA')) return { bg: '#e3f2fd', color: '#1565c0' };
  if (d.includes('COPA') || d.includes('COPARTICIPA')) return { bg: '#fff3e0', color: '#e65100' };
  if (d.includes('FAP')) return { bg: '#f3e5f5', color: '#6a1b9a' };
  if (d.includes('PARCELAMENTO')) return { bg: '#e8f5e9', color: '#2e7d32' };
  return { bg: '#f5f5f5', color: '#555' };
};

// ---------------------------------------------------------------------------
// Boleto com itens expansíveis
// ---------------------------------------------------------------------------
const BoletoDetalhe = ({ boleto }) => {
  const [aberto, setAberto] = useState(false);
  const cor = atrasoColor(boleto.dias);
  const temItens = boleto.itens && boleto.itens.length > 0;

  return (
    <div style={{
      background: '#fff', borderRadius: 8, marginBottom: 6,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
      border: '1px solid #eee',
    }}>
      {/* Cabeçalho do boleto */}
      <div
        onClick={() => temItens && setAberto(!aberto)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.5rem 0.75rem', fontSize: '0.8rem',
          cursor: temItens ? 'pointer' : 'default',
          background: aberto ? '#f8fffe' : '#fff',
        }}
      >
        {temItens && (
          <span style={{ color: '#aaa', fontSize: '0.75rem' }}>
            {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
        {/* Número do boleto */}
        <span style={{ fontFamily: 'monospace', color: '#555', fontSize: '0.78rem', minWidth: 80 }}>
          #{boleto.num}
        </span>
        {/* Referência */}
        <span style={{ color: '#888', fontSize: '0.75rem', minWidth: 60 }}>{boleto.refer}</span>
        {/* Tipos de dívida */}
        <div style={{ display: 'flex', gap: 3, flex: 1, flexWrap: 'wrap' }}>
          {boleto.tipos && boleto.tipos.map((t, i) => {
            const s = tipoBadgeStyle(t);
            return (
              <span key={i} style={{
                fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                padding: '0.1rem 0.4rem', borderRadius: 3,
                background: s.bg, color: s.color,
              }}>{t}</span>
            );
          })}
        </div>
        {/* Contrato */}
        {boleto.contrato && (
          <span style={{ fontSize: '0.72rem', color: '#aaa' }}>Contrato {boleto.contrato}</span>
        )}
        {/* Vencimento */}
        <span style={{ fontSize: '0.75rem', color: '#666', minWidth: 80, textAlign: 'right' }}>
          Venc: {boleto.venc}
        </span>
        {/* Badge de atraso */}
        <span style={{
          background: cor, color: '#fff', borderRadius: 4,
          padding: '0.15rem 0.5rem', fontWeight: 700, fontSize: '0.75rem', minWidth: 55, textAlign: 'center',
        }}>
          {boleto.dias}d
        </span>
        {/* Status */}
        <span style={{
          background: boleto.status === 'P' ? '#3498db22' : '#f39c1222',
          color: boleto.status === 'P' ? '#2980b9' : '#e67e22',
          borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.72rem', fontWeight: 600,
        }}>
          {boleto.status === 'P' ? 'Parcial' : 'Aberto'}
        </span>
        {/* Valor total */}
        <span style={{ fontWeight: 700, color: '#c0392b', fontSize: '0.88rem', minWidth: 90, textAlign: 'right' }}>
          {formatMoeda(boleto.val)}
        </span>
      </div>

      {/* Itens do boleto (expandido) */}
      {aberto && temItens && (
        <div style={{ borderTop: '1px solid #f0f0f0', background: '#fafcfb' }}>
          {boleto.itens.map((item, i) => {
            const s = tipoBadgeStyle(item.desc);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '0.3rem 0.75rem 0.3rem 2.5rem',
                borderBottom: i < boleto.itens.length - 1 ? '1px solid #f5f5f5' : 'none',
                fontSize: '0.78rem', color: '#555',
              }}>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                  padding: '0.1rem 0.4rem', borderRadius: 3,
                  background: s.bg, color: s.color, minWidth: 80, textAlign: 'center',
                }}>
                  {item.desc}
                </span>
                {item.matricula && (
                  <span style={{ color: '#aaa', fontSize: '0.72rem' }}>Mat: {item.matricula}</span>
                )}
                {item.lancamento && (
                  <span style={{ color: '#aaa', fontSize: '0.72rem' }}>Lanç: {item.lancamento}</span>
                )}
                <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#333' }}>
                  {formatMoeda(item.val)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Linha expandível do Top 50
// ---------------------------------------------------------------------------
const RiscoRow = ({ rf, idx }) => {
  const [aberto, setAberto] = useState(false);
  const [boletos, setBoletos] = useState(null);
  const [loadingBoletos, setLoadingBoletos] = useState(false);
  const [iaResultado, setIaResultado] = useState(null);
  const [loadingIa, setLoadingIa] = useState(false);

  const handleExpandir = async () => {
    const novoAberto = !aberto;
    setAberto(novoAberto);
    if (novoAberto && boletos === null) {
      setLoadingBoletos(true);
      try {
        const res = await inadimplenciaAPI.boletosRf(rf.mat);
        setBoletos(res.data.boletos || []);
      } catch {
        setBoletos([]);
      } finally {
        setLoadingBoletos(false);
      }
    }
  };

  const handleAnaliseIa = async () => {
    setLoadingIa(true);
    setIaResultado(null);
    try {
      const res = await inadimplenciaAPI.analiseIa(rf, boletos || []);
      setIaResultado(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na análise de IA');
    } finally {
      setLoadingIa(false);
    }
  };

  const qtdParcelas = boletos ? boletos.length : rf.qtd_boletos;
  const maiorAtraso = boletos
    ? (boletos.length > 0 ? Math.max(...boletos.map(b => b.dias)) : 0)
    : rf.dias_geral;

  return (
    <React.Fragment>
      <tr style={{
        borderBottom: aberto ? 'none' : '1px solid #f5f5f5',
        background: rf.cancelavel ? '#fff5f5' : 'transparent',
        cursor: 'pointer',
      }} onClick={handleExpandir}>
        {/* # */}
        <td style={{ padding: '0.45rem 0.75rem', color: '#aaa', fontWeight: 600, fontSize: '0.8rem' }}>
          {idx + 1}
        </td>
        {/* Ícone expand */}
        <td style={{ padding: '0.45rem 0.5rem', width: 28 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 20, height: 20, borderRadius: '50%',
            background: aberto ? 'var(--brand, #20a490)' : '#f0f0f0',
            color: aberto ? '#fff' : '#666',
            fontSize: '1rem', fontWeight: 700, lineHeight: 1,
            transition: 'all 0.15s',
          }}>
            {aberto ? '−' : '+'}
          </span>
        </td>
        {/* Nome */}
        <td style={{ padding: '0.45rem 0.75rem' }}>
          <div style={{ fontWeight: 600, color: '#222', fontSize: '0.85rem' }}>{rf.nome || '—'}</div>
          <div style={{ fontSize: '0.7rem', color: '#aaa' }}>Mat: {rf.mat}</div>
        </td>
        {/* Score */}
        <td style={{ padding: '0.45rem 0.75rem', minWidth: 120 }}>
          <ScoreBar score={rf.score} />
        </td>
        {/* Mens */}
        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'center' }}>
          {rf.dias_mens > 0 ? (
            <span style={{
              background: atrasoColor(rf.dias_mens) + '22',
              color: atrasoColor(rf.dias_mens),
              borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 600, fontSize: '0.78rem',
            }}>{rf.dias_mens}d</span>
          ) : <span style={{ color: '#ccc' }}>—</span>}
        </td>
        {/* Copa */}
        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'center' }}>
          {rf.dias_copa > 0 ? (
            <span style={{
              background: '#e6510022', color: '#e65100',
              borderRadius: 4, padding: '0.1rem 0.4rem', fontWeight: 600, fontSize: '0.78rem',
            }}>{rf.dias_copa}d</span>
          ) : <span style={{ color: '#ccc' }}>—</span>}
        </td>
        {/* Total */}
        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#c0392b', fontSize: '0.88rem' }}>
          {formatMoeda(rf.vl_total)}
        </td>
        {/* Status */}
        <td style={{ padding: '0.45rem 0.75rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
            {rf.cancelavel && (
              <span style={{ background: '#e74c3c', color: '#fff', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.68rem', fontWeight: 700 }}>
                CANCELÁVEL
              </span>
            )}
            {rf.anomalia === 'copa_sem_mensalidade' && (
              <span style={{ background: '#e6510022', color: '#e65100', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.68rem', fontWeight: 700, border: '1px solid #e65100' }}>
                SÓ COPA
              </span>
            )}
            {rf.anomalia === 'copa_dominante' && (
              <span style={{ background: '#9b59b622', color: '#8e44ad', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.68rem', fontWeight: 700, border: '1px solid #8e44ad' }}>
                COPA DOM.
              </span>
            )}
            {!rf.cancelavel && !rf.anomalia && <span style={{ color: '#ccc', fontSize: '0.72rem' }}>—</span>}
          </div>
        </td>
      </tr>

      {/* Linha expandida */}
      {aberto && (
        <tr style={{ background: '#f8fbfa' }}>
          <td colSpan={8} style={{ padding: '0 0 1rem 0', borderBottom: '2px solid #e0f0ec' }}>
            <div style={{ padding: '1rem 1rem 0.5rem 3rem' }}>

              {loadingBoletos ? (
                <div style={{ color: '#aaa', fontSize: '0.82rem', padding: '1rem 0' }}>
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />
                  Carregando boletos...
                </div>
              ) : (
                <>
                  {/* Cards de resumo com breakdown */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Parcelas em atraso', value: qtdParcelas, color: '#e67e22' },
                      { label: 'Maior atraso', value: `${maiorAtraso} dias`, color: atrasoColor(maiorAtraso) },
                    ].map((card) => (
                      <div key={card.label} style={{
                        background: '#fff', borderRadius: 8, padding: '0.6rem 1rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: 140,
                        borderLeft: `3px solid ${card.color}`,
                      }}>
                        <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 2 }}>{card.label}</div>
                        <div style={{ fontWeight: 700, color: card.color, fontSize: '1.1rem' }}>{card.value}</div>
                      </div>
                    ))}
                    {/* Card Total com breakdown */}
                    <div style={{
                      background: '#fff', borderRadius: 8, padding: '0.6rem 1rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: 260,
                      borderLeft: '3px solid #c0392b', flex: 1,
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 4 }}>Total devido</div>
                      <div style={{ fontWeight: 700, color: '#c0392b', fontSize: '1.1rem', marginBottom: 6 }}>
                        {formatMoeda(rf.vl_total)}
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
                        {rf.vl_mens > 0 && (
                          <div>
                            <span style={{ color: '#888' }}>Mensalidade: </span>
                            <span style={{ fontWeight: 600, color: '#e67e22' }}>{formatMoeda(rf.vl_mens)}</span>
                          </div>
                        )}
                        {rf.vl_copa > 0 && (
                          <div>
                            <span style={{ color: '#888' }}>Coparticipação: </span>
                            <span style={{ fontWeight: 600, color: '#8e44ad' }}>{formatMoeda(rf.vl_copa)}</span>
                          </div>
                        )}
                        {rf.vl_fap > 0 && (
                          <div>
                            <span style={{ color: '#888' }}>FAP: </span>
                            <span style={{ fontWeight: 600, color: '#2980b9' }}>{formatMoeda(rf.vl_fap)}</span>
                          </div>
                        )}
                        {(() => {
                          const vl_outros = rf.vl_total - (rf.vl_mens || 0) - (rf.vl_copa || 0) - (rf.vl_fap || 0);
                          return vl_outros > 0.01 ? (
                            <div>
                              <span style={{ color: '#888' }}>Taxa/Outros: </span>
                              <span style={{ fontWeight: 600, color: '#7f8c8d' }}>{formatMoeda(vl_outros)}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Lista de boletos agrupados */}
                  {boletos && boletos.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      {boletos.map((b, i) => (
                        <BoletoDetalhe key={i} boleto={b} />
                      ))}
                    </div>
                  )}

                  {/* Botão analisar IA + resultado */}
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAnaliseIa(); }}
                      disabled={loadingIa}
                      style={{
                        background: loadingIa ? '#ccc' : '#6c3483',
                        color: '#fff', border: 'none', borderRadius: 6,
                        padding: '0.45rem 1.1rem', fontSize: '0.82rem',
                        fontWeight: 600, cursor: loadingIa ? 'not-allowed' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        marginBottom: iaResultado ? '0.75rem' : 0,
                      }}
                    >
                      {loadingIa
                        ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Analisando...</>
                        : '✦ Analisar com IA'}
                    </button>

                    {iaResultado && (
                      <div style={{
                        background: '#fff', borderRadius: 8, padding: '1rem',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        borderLeft: '3px solid #6c3483',
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem',
                      }}>
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6c3483', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Fatores de Risco
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#444', lineHeight: 1.7 }}>
                            {(iaResultado.fatores_risco || []).map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--brand, #20a490)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Ações de Cobrança
                          </div>
                          <ol style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#444', lineHeight: 1.7 }}>
                            {(iaResultado.acoes_cobranca || []).map((a, i) => (
                              <li key={i}>{String(a).replace(/^\d+\.\s*/, '')}</li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// ---------------------------------------------------------------------------
// Aba Análise
// ---------------------------------------------------------------------------
const LIMITES = [50, 100, 200, 500];
const FILTROS = [
  { key: '',             label: 'Todos'          },
  { key: 'cancelavel',   label: 'Cancelável'     },
  { key: 'alto_risco',   label: 'Alto Risco (≥70)' },
  { key: 'so_copa',      label: 'Só Coparticipação' },
  { key: 'copa_dominante', label: 'Copa Dominante' },
];

const AbaAnalise = ({ isAnalistaUp }) => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gerandoSnapshot, setGerandoSnapshot] = useState(false);
  const [limite, setLimite] = useState(50);
  const [filtroAtivo, setFiltroAtivo] = useState('');

  const carregar = useCallback(async (lim, fil) => {
    setLoading(true);
    try {
      const res = await inadimplenciaAPI.analise(lim, fil);
      setDados(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Acesso restrito a analistas e superiores');
      } else {
        toast.error('Erro ao carregar análise');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFiltro = (key) => {
    setFiltroAtivo(key);
    carregar(limite, key);
  };

  const handleLimite = (lim) => {
    setLimite(lim);
    carregar(lim, filtroAtivo);
  };

  useEffect(() => { carregar(limite, filtroAtivo); }, [carregar]);

  const handleSnapshot = async () => {
    setGerandoSnapshot(true);
    try {
      const res = await inadimplenciaAPI.snapshotDiario();
      toast.success(`Snapshot criado: ${res.data.registros} registros`);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar snapshot');
    } finally {
      setGerandoSnapshot(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem', color: '#aaa' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: 12 }}>Carregando análise...</div>
      </div>
    );
  }

  if (!dados) return null;

  const { aging_matrix, totais, top_risco, anomalia_copa, tendencia, tem_historico } = dados;

  return (
    <div>
      {/* Header ação */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: 8 }}>
        <button
          onClick={carregar}
          style={{
            background: 'none',
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: '0.4rem 0.9rem',
            fontSize: '0.82rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            color: '#555',
          }}
        >
          <RefreshCw size={13} />
          Atualizar
        </button>
        <button
          onClick={handleSnapshot}
          disabled={gerandoSnapshot}
          style={{
            background: gerandoSnapshot ? '#ccc' : '#3498db',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '0.4rem 0.9rem',
            cursor: gerandoSnapshot ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: '0.82rem',
            fontWeight: 600,
          }}
          title="Salva snapshot do dia atual para construir histórico de tendência"
        >
          <TrendingUp size={13} />
          {gerandoSnapshot ? 'Gerando...' : 'Gerar Snapshot Hoje'}
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <KpiCard title="Total Inadimplentes" value={totais.total_rfs.toLocaleString('pt-BR')} color="#e67e22" />
        <KpiCard
          title="Críticos (>60d mensalidade)"
          value={totais.total_criticos.toLocaleString('pt-BR')}
          sub={`${Math.round((totais.total_criticos / (totais.total_rfs || 1)) * 100)}% do total`}
          color="#e74c3c"
        />
        <KpiCard
          title="Exposição Cancelável"
          value={formatMoeda(totais.cancelaveis_valor)}
          sub={`${totais.cancelaveis} RFs com mens. >60d`}
          color="#8e1a0e"
        />
        <KpiCard
          title="Só Coparticipação >60d"
          value={totais.so_copa_criticos.toLocaleString('pt-BR')}
          sub="Sem mensalidade em atraso"
          color="#e65100"
        />
      </div>

      {/* Aging Matrix */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: '1.25rem',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222' }}>
            Matriz de Aging
          </div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
            RFs e valor por faixa de atraso e tipo de débito
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '0.6rem 1rem', textAlign: 'left', color: '#555', fontWeight: 600 }}>Faixa</th>
                {tiposAnalise.map((t) => (
                  <th key={t.key} style={{ padding: '0.6rem 1rem', textAlign: 'center', color: t.color, fontWeight: 600 }}>
                    {t.label}
                  </th>
                ))}
                <th style={{ padding: '0.6rem 1rem', textAlign: 'center', color: '#333', fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {aging_matrix.map((row) => {
                const fc = faixasAnalise.find((f) => f.key === row.faixa) || {};
                return (
                  <tr key={row.faixa} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      <span style={{
                        background: `${fc.color}22`,
                        color: fc.color,
                        borderRadius: 4,
                        padding: '0.15rem 0.6rem',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                      }}>
                        {fc.label || row.faixa}
                      </span>
                    </td>
                    {tiposAnalise.map((t) => {
                      const cell = row.por_tipo?.[t.key] || { rfs: 0, valor: 0 };
                      return (
                        <td key={t.key} style={{ padding: '0.6rem 1rem', textAlign: 'center' }}>
                          {cell.rfs > 0 ? (
                            <>
                              <div style={{ fontWeight: 700, color: t.color }}>{cell.rfs}</div>
                              <div style={{ fontSize: '0.72rem', color: '#888' }}>{formatMoeda(cell.valor)}</div>
                            </>
                          ) : (
                            <span style={{ color: '#ddd' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ padding: '0.6rem 1rem', textAlign: 'center', background: '#fafafa' }}>
                      <div style={{ fontWeight: 700, color: fc.color || '#333' }}>{row.total_rfs}</div>
                      <div style={{ fontSize: '0.72rem', color: '#666' }}>{formatMoeda(row.total_valor)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Radar de Coparticipação */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        padding: '1rem 1.25rem',
        marginBottom: '1.25rem',
        borderLeft: '4px solid #e65100',
      }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: 4 }}>
          Radar de Coparticipação
        </div>
        <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
          Beneficiários com coparticipação em atraso <strong>sem mensalidade em aberto</strong>: possível comportamento estratégico
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e65100' }}>
              {anomalia_copa.total_so_copa}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#888' }}>RFs só coparticipação</div>
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e74c3c' }}>
              {anomalia_copa.criticos_so_copa}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#888' }}>Críticos (>60 dias)</div>
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#c0392b' }}>
              {formatMoeda(anomalia_copa.vl_so_copa)}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#888' }}>Valor total exposto</div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.75rem', color: '#555', lineHeight: 1.5 }}>
              <strong>Atenção:</strong> Coparticipação em atraso <em>não</em> gera obrigação de cancelamento.
              Estes RFs podem estar aproveitando essa brecha. Priorize cobrança ativa e avalie se há padrão de uso elevado.
            </div>
          </div>
        </div>
      </div>

      {/* Top 50 Score de Risco */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: '1.25rem',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222' }}>
                Top {limite} por Score de Risco
                {filtroAtivo && (
                  <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 600, color: '#3498db' }}>
                    [{FILTROS.find(f => f.key === filtroAtivo)?.label}]
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
                Score 0–100 ponderado por valor, tipo e tempo de atraso. Vermelho = cancelável (mens. &gt;60 dias).
              </div>
            </div>
            {/* Seletor de limite */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>Exibir:</span>
              {LIMITES.map((l) => (
                <button key={l} onClick={() => handleLimite(l)} style={{
                  padding: '0.2rem 0.6rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: limite === l ? 'var(--brand, #20a490)' : '#ddd',
                  background: limite === l ? 'var(--brand, #20a490)' : '#fff',
                  color: limite === l ? '#fff' : '#666',
                }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros por status */}
          <div style={{ display: 'flex', gap: 6, marginTop: '0.75rem', flexWrap: 'wrap' }}>
            {FILTROS.map((f) => {
              const contagem = dados?.contagens_filtro?.[f.key || 'todos'] ?? '';
              const ativo = filtroAtivo === f.key;
              const cor = f.key === 'cancelavel' ? '#e74c3c'
                : f.key === 'alto_risco' ? '#e67e22'
                : f.key === 'so_copa' ? '#e65100'
                : f.key === 'copa_dominante' ? '#8e44ad'
                : 'var(--brand, #20a490)';
              return (
                <button key={f.key} onClick={() => handleFiltro(f.key)} style={{
                  padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                  cursor: 'pointer', border: `1px solid ${ativo ? cor : '#ddd'}`,
                  background: ativo ? cor : '#fff',
                  color: ativo ? '#fff' : '#555',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  {f.label}
                  {contagem !== '' && (
                    <span style={{
                      background: ativo ? 'rgba(255,255,255,0.3)' : `${cor}22`,
                      color: ativo ? '#fff' : cor,
                      borderRadius: 10, padding: '0 5px', fontSize: '0.72rem', fontWeight: 700,
                    }}>
                      {contagem}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#555', fontWeight: 600, width: 30 }}>#</th>
                <th style={{ padding: '0.5rem 0.5rem', width: 28 }}></th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#555', fontWeight: 600 }}>Nome</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#555', fontWeight: 600, minWidth: 120 }}>Score</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#555', fontWeight: 600 }}>Mens.</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#555', fontWeight: 600 }}>Copa.</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#555', fontWeight: 600 }}>Total</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#555', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {top_risco.map((rf, idx) => (
                <RiscoRow key={rf.mat} rf={rf} idx={idx} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tendência histórica */}
      {tem_historico && tendencia.length >= 2 ? (
        <div style={{
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          padding: '1rem 1.25rem',
          marginBottom: '1.25rem',
        }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: 4 }}>
            Tendência Histórica
          </div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.75rem' }}>
            Evolução do valor total de inadimplência (snapshots diários)
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1.5rem', flexWrap: 'wrap' }}>
            <Sparkline dados={tendencia} height={70} color="var(--brand, #20a490)" />
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                Primeiro snapshot: {tendencia[0]?.data}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                Último: {tendencia[tendencia.length - 1]?.data}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>
                {tendencia.length} snapshots registrados
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          background: '#f8f9fa',
          borderRadius: 10,
          padding: '1rem 1.25rem',
          marginBottom: '1.25rem',
          fontSize: '0.82rem',
          color: '#888',
          borderLeft: '3px solid #ddd',
        }}>
          <TrendingUp size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          O gráfico de tendência aparecerá após 2 ou mais snapshots diários. Clique em "Gerar Snapshot Hoje" para iniciar o histórico.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
const KpiCard = ({ title, value, sub, color }) => (
  <div style={{
    background: '#fff',
    borderRadius: 10,
    padding: '1rem 1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color || 'var(--brand, #20a490)'}`,
    flex: 1,
    minWidth: 160,
  }}>
    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: color || 'var(--brand, #20a490)' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// Faixa bar clicavel
// ---------------------------------------------------------------------------
const FaixaBar = ({ porFaixa, faixaAtiva, onFaixaClick }) => {
  const total = porFaixa.reduce((acc, f) => acc + f.rfs, 0);
  return (
    <div style={{
      background: '#fff',
      borderRadius: 10,
      padding: '1rem 1.25rem',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      marginBottom: '1.25rem',
    }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', marginBottom: '0.75rem' }}>
        Faixas de Atraso (por mensalidade)
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {faixaConfig.map((fc) => {
          const dado = porFaixa.find((f) => f.faixa === fc.key) || { rfs: 0, valor: 0 };
          const pct = total > 0 ? Math.round((dado.rfs / total) * 100) : 0;
          const ativo = faixaAtiva === fc.key;
          return (
            <div
              key={fc.key}
              onClick={() => onFaixaClick(ativo ? '' : fc.key)}
              style={{
                flex: 1,
                minWidth: 120,
                background: ativo ? fc.color : `${fc.color}22`,
                border: `2px solid ${fc.color}`,
                borderRadius: 8,
                padding: '0.6rem 0.75rem',
                cursor: 'pointer',
                transition: 'all 0.15s',
                textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: ativo ? '#fff' : fc.color }}>
                {dado.rfs}
              </div>
              <div style={{ fontSize: '0.7rem', color: ativo ? '#ffffffcc' : '#555', fontWeight: 500 }}>
                {fc.label}
              </div>
              <div style={{ fontSize: '0.7rem', color: ativo ? '#ffffffaa' : '#888' }}>
                {formatMoeda(dado.valor)}
              </div>
              <div style={{
                marginTop: 4,
                background: ativo ? 'rgba(255,255,255,0.3)' : `${fc.color}44`,
                borderRadius: 4,
                height: 4,
                overflow: 'hidden',
              }}>
                <div style={{ width: `${pct}%`, height: '100%', background: ativo ? '#fff' : fc.color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Linha de item de boleto
// ---------------------------------------------------------------------------
const ItemRow = ({ item }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.35rem 1rem',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '0.8rem',
    color: '#555',
  }}>
    <span style={{ flex: 2, fontWeight: 500 }}>{item.desc_divida || '—'}</span>
    <span style={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>{formatMoeda(item.vl_item)}</span>
    <span style={{ flex: 1, color: '#888' }}>
      {item.cd_matricula ? `Matrícula: ${item.cd_matricula}` : ''}
      {item.cod_lancamento ? `  Lanç.: ${item.cod_lancamento}` : ''}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Linha de boleto (expansivel)
// ---------------------------------------------------------------------------
const BoletoRow = ({ boleto }) => {
  const [aberto, setAberto] = useState(false);
  const cor = atrasoColor(boleto.atraso_dias || 0);

  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div
        onClick={() => setAberto(!aberto)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.5rem 1rem',
          cursor: 'pointer',
          background: aberto ? '#fafafa' : 'transparent',
          fontSize: '0.82rem',
        }}
      >
        {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ flex: 2, fontFamily: 'monospace', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Boleto {boleto.cd_mens_contrato}
          {boleto.desc_divida && (
            <span style={{
              fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
              padding: '0.1rem 0.45rem', borderRadius: '4px',
              background: boleto.desc_divida.toUpperCase().includes('MENSALIDADE') ? '#e3f2fd' : boleto.desc_divida.toUpperCase().includes('COPARTICIPACAO') || boleto.desc_divida.toUpperCase().includes('COPARTICIPAÇÃO') ? '#fff3e0' : '#f3e5f5',
              color: boleto.desc_divida.toUpperCase().includes('MENSALIDADE') ? '#1565c0' : boleto.desc_divida.toUpperCase().includes('COPARTICIPACAO') || boleto.desc_divida.toUpperCase().includes('COPARTICIPAÇÃO') ? '#e65100' : '#6a1b9a',
            }}>
              {boleto.desc_divida}
            </span>
          )}
        </span>
        <span style={{ flex: 1, color: '#555' }}>{boleto.refer}</span>
        <span style={{ flex: 1.5, color: '#666' }}>
          {boleto.dt_vencimento ? `Venc: ${boleto.dt_vencimento}` : ''}
        </span>
        <span style={{
          flex: 0.8,
          background: cor,
          color: '#fff',
          borderRadius: 4,
          padding: '0.15rem 0.5rem',
          fontWeight: 600,
          textAlign: 'center',
          fontSize: '0.75rem',
        }}>
          {boleto.atraso_dias} dias
        </span>
        <span style={{
          flex: 0.5,
          background: boleto.status === 'P' ? '#3498db22' : '#f39c1222',
          color: boleto.status === 'P' ? '#2980b9' : '#e67e22',
          borderRadius: 4,
          padding: '0.1rem 0.4rem',
          fontSize: '0.72rem',
          textAlign: 'center',
          fontWeight: 600,
        }}>
          {boleto.status === 'P' ? 'Parcial' : 'Aberto'}
        </span>
        <span style={{ flex: 1, textAlign: 'right', fontWeight: 700, color: '#333' }}>
          {formatMoeda(boleto.vl_a_cobrar)}
        </span>
      </div>
      {aberto && boleto.itens && boleto.itens.length > 0 && (
        <div style={{ background: '#f9fafb', borderTop: '1px solid #eee' }}>
          {boleto.itens.map((item, idx) => (
            <ItemRow key={idx} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Linha de RF (expansivel)
// ---------------------------------------------------------------------------
const RFRow = ({ rf, onAtendimento, onEnviarEmail }) => {
  const [aberto, setAberto] = useState(false);
  const cor = atrasoColor(rf.maior_atraso_mensalidade || 0);
  const alerta = rf.maior_atraso_mensalidade > 60;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e8e8e8',
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Cabecalho do RF */}
      <div
        onClick={() => setAberto(!aberto)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ color: '#aaa', flexShrink: 0 }}>
          {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>

        {/* Nome */}
        <div style={{ flex: 3, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {rf.nome}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 1 }}>
            {rf.celular && <span style={{ marginRight: 8 }}>📱 {rf.celular}</span>}
            {rf.email && <span>{rf.email}</span>}
          </div>
        </div>

        {/* Tipo cobrança */}
        <span style={{
          background: rf.tp_cobranca === 'B' ? '#3498db22' : '#9b59b622',
          color: rf.tp_cobranca === 'B' ? '#2980b9' : '#8e44ad',
          borderRadius: 4,
          padding: '0.15rem 0.5rem',
          fontSize: '0.72rem',
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {rf.tp_cobranca === 'B' ? 'Boleto' : rf.tp_cobranca === 'C' ? 'Carnê' : rf.tp_cobranca || '—'}
        </span>

        {/* Ativo */}
        <span style={{
          background: rf.sn_ativo === 'S' ? '#27ae6022' : '#e74c3c22',
          color: rf.sn_ativo === 'S' ? '#27ae60' : '#e74c3c',
          borderRadius: 4,
          padding: '0.15rem 0.5rem',
          fontSize: '0.72rem',
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {rf.sn_ativo === 'S' ? 'Ativo' : 'Inativo'}
        </span>

        {/* Qtd mensalidades */}
        <span style={{ fontSize: '0.8rem', color: '#666', flexShrink: 0 }}>
          {rf.qtd_mensalidades_atraso} mens.
        </span>

        {/* Badge atraso */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <span style={{
            background: cor,
            color: '#fff',
            borderRadius: 5,
            padding: '0.2rem 0.6rem',
            fontWeight: 700,
            fontSize: '0.8rem',
          }}>
            {rf.maior_atraso_mensalidade} dias
          </span>
          {alerta && <span title="Atraso crítico" style={{ color: '#e74c3c', fontSize: '1rem' }}>⚠</span>}
        </div>

        {/* Valor total */}
        <span style={{ fontWeight: 700, color: '#c0392b', fontSize: '0.95rem', flexShrink: 0, minWidth: 100, textAlign: 'right' }}>
          {formatMoeda(rf.total_valor)}
        </span>

        {/* Botoes - parar propagacao do click */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onAtendimento(rf)}
            style={{
              background: 'var(--brand, #20a490)',
              color: 'var(--brand-ink, #fff)',
              border: 'none',
              borderRadius: 5,
              padding: '0.3rem 0.65rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="Novo atendimento"
          >
            Atendimento
          </button>
          <button
            onClick={() => onEnviarEmail && onEnviarEmail(rf)}
            style={{
              background: '#fff',
              color: '#3498db',
              border: '1px solid #3498db',
              borderRadius: 5,
              padding: '0.3rem 0.65rem',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
            title="Enviar email de notificação"
          >
            Email
          </button>
        </div>
      </div>

      {/* CPF linha secundaria */}
      <div style={{
        paddingLeft: '3rem',
        paddingBottom: '0.4rem',
        fontSize: '0.75rem',
        color: '#aaa',
        marginTop: -8,
      }}>
        CPF: {formatCPF(rf.cpf)} &nbsp;|&nbsp; Mat. RF: {rf.mat_resp_fin}
        {rf.lote ? ` | Lote: ${rf.lote}` : ''}
      </div>

      {/* Boletos expandidos */}
      {aberto && (
        <div style={{ borderTop: '1px solid #eee', background: '#fafafa' }}>
          {rf.boletos && rf.boletos.length > 0 ? (
            rf.boletos.map((boleto, idx) => (
              <BoletoRow key={idx} boleto={boleto} />
            ))
          ) : (
            <div style={{ padding: '0.75rem 1rem', color: '#aaa', fontSize: '0.82rem' }}>
              Nenhum boleto encontrado.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const DashboardInadimplencia = () => {
  const navigate = useNavigate();
  const { temNivel } = useAuth();
  // Níveis do módulo (ADR 0013 / #78): gestor envia cobrança; operador analisa.
  const isGestorUp = temNivel('dashboard_inadimplencia', 'gestor');
  const isAnalistaUp = temNivel('dashboard_inadimplencia', 'operador');

  const [abaAtiva, setAbaAtiva] = useState('lista');
  const [resumo, setResumo] = useState(null);
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 50;

  const [loadingResumo, setLoadingResumo] = useState(true);
  const [loadingLista, setLoadingLista] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [ultimaSync, setUltimaSync] = useState(null);

  // Filtros — `?busca=` na URL permite abrir a tela JA FILTRADA (ex.: o atalho
  // da ficha do beneficiario manda a matricula do responsavel financeiro).
  // Sem isso, o atalho caia na lista inteira e o operador tinha que buscar na mao.
  const buscaInicial = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('busca') || '';
    } catch (_) {
      return '';
    }
  }, []);
  const [busca, setBusca] = useState(buscaInicial);
  const [buscaInput, setBuscaInput] = useState(buscaInicial);
  const [faixaAtiva, setFaixaAtiva] = useState('');
  const [snAtivo, setSnAtivo] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [tpCobranca, setTpCobranca] = useState('');

  // Modal envio email
  const [emailModalRf, setEmailModalRf] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [emailEnviando, setEmailEnviando] = useState(false);
  const [emailLoadingTemplates, setEmailLoadingTemplates] = useState(false);

  // ---------------------------------------------------------------------------
  // Carregar resumo KPIs
  // ---------------------------------------------------------------------------
  const carregarResumo = useCallback(async () => {
    setLoadingResumo(true);
    try {
      const res = await api.get('/inadimplencia/resumo');
      setResumo(res.data);
      if (res.data.ultima_sync) {
        setUltimaSync(new Date(res.data.ultima_sync));
      }
    } catch (err) {
      toast.error('Erro ao carregar resumo de inadimplência');
      console.error(err);
    } finally {
      setLoadingResumo(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Carregar lista de RFs
  // ---------------------------------------------------------------------------
  const carregarLista = useCallback(async () => {
    setLoadingLista(true);
    try {
      const params = {
        page,
        per_page: perPage,
      };
      if (busca) params.busca = busca;
      if (faixaAtiva) params.faixa = faixaAtiva;
      if (snAtivo) params.sn_ativo = snAtivo;
      if (statusFiltro) params.status = statusFiltro;
      if (tpCobranca) params.tp_cobranca = tpCobranca;

      const res = await api.get('/inadimplencia/lista', { params });
      setLista(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Erro ao carregar lista de inadimplentes');
      console.error(err);
    } finally {
      setLoadingLista(false);
    }
  }, [page, busca, faixaAtiva, snAtivo, statusFiltro, tpCobranca]);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleBuscaSubmit = (e) => {
    e.preventDefault();
    setBusca(buscaInput);
    setPage(1);
  };

  const handleFaixaClick = (f) => {
    setFaixaAtiva(f);
    setPage(1);
  };

  const handleLimparFiltros = () => {
    setBusca('');
    setBuscaInput('');
    setFaixaAtiva('');
    setSnAtivo('');
    setStatusFiltro('');
    setTpCobranca('');
    setPage(1);
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const res = await api.post('/inadimplencia/sincronizar');
      setUltimaSync(new Date(res.data.sincronizado_em));
      toast.success(`Sincronizado: ${res.data.total_registros.toLocaleString('pt-BR')} registros`);
      await carregarResumo();
      await carregarLista();
    } catch (err) {
      toast.error('Erro na sincronização');
      console.error(err);
    } finally {
      setSincronizando(false);
    }
  };

  const handleAtendimento = (rf) => {
    navigate('/kanban', {
      state: {
        abrirNovoAtendimento: true,
        beneficiario: {
          nome_completo: rf.nome,
          cpf: rf.cpf,
        },
      },
    });
  };

  const handleEnviarEmail = async (rf) => {
    if (!isGestorUp) {
      toast.error('Sem permissão para enviar notificações');
      return;
    }
    setEmailModalRf(rf);
    setEmailTemplateId('');
    setEmailLoadingTemplates(true);
    try {
      const res = await emailTemplatesAPI.listar();
      setEmailTemplates(res.data.filter(t => t.ativo));
    } catch (err) {
      toast.error('Erro ao carregar templates');
    }
    setEmailLoadingTemplates(false);
  };

  const handleConfirmarEnvio = async () => {
    if (!emailTemplateId || !emailModalRf) return;
    setEmailEnviando(true);
    try {
      const res = await emailTemplatesAPI.enviar(emailTemplateId, [emailModalRf.mat_resp_fin]);
      const resultado = res.data;
      if (resultado.total_enviados > 0) {
        toast.success(`Notificação enviada para ${emailModalRf.email || emailModalRf.nome}`);
      } else {
        const detalhe = resultado.detalhes?.[0]?.detalhe || 'Erro desconhecido';
        toast.error(`Falha: ${detalhe}`);
      }
      setEmailModalRf(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar notificação');
    }
    setEmailEnviando(false);
  };

  const totalPages = Math.ceil(total / perPage);
  const inicioReg = (page - 1) * perPage + 1;
  const fimReg = Math.min(page * perPage, total);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Titulo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <AlertCircle size={24} color="#e74c3c" />
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#222', margin: 0 }}>
          Dashboard de Inadimplência
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {ultimaSync && (
            <span style={{ fontSize: '0.75rem', color: '#999' }}>
              Atualizado em {ultimaSync.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={handleSincronizar}
            disabled={sincronizando}
            style={{
              background: sincronizando ? '#ccc' : 'var(--brand, #20a490)',
              color: sincronizando ? '#fff' : 'var(--brand-ink, #fff)',
              border: 'none',
              borderRadius: 6,
              padding: '0.4rem 0.9rem',
              cursor: sincronizando ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: '0.82rem',
              fontWeight: 600,
            }}
          >
            <RefreshCw size={14} style={{ animation: sincronizando ? 'spin 1s linear infinite' : 'none' }} />
            {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      </div>

      {/* Abas */}
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: '1.25rem',
        borderBottom: '2px solid #e8e8e8',
      }}>
        <button
          onClick={() => setAbaAtiva('lista')}
          style={{
            padding: '0.55rem 1.25rem',
            background: 'none',
            border: 'none',
            borderBottom: abaAtiva === 'lista' ? '2px solid var(--brand, #20a490)' : '2px solid transparent',
            marginBottom: -2,
            cursor: 'pointer',
            fontWeight: abaAtiva === 'lista' ? 700 : 400,
            color: abaAtiva === 'lista' ? 'var(--brand, #20a490)' : '#666',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <AlertCircle size={14} />
          Inadimplentes
        </button>
        {isAnalistaUp && (
          <button
            onClick={() => setAbaAtiva('analise')}
            style={{
              padding: '0.55rem 1.25rem',
              background: 'none',
              border: 'none',
              borderBottom: abaAtiva === 'analise' ? '2px solid #3498db' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
              fontWeight: abaAtiva === 'analise' ? 700 : 400,
              color: abaAtiva === 'analise' ? '#3498db' : '#666',
              fontSize: '0.88rem',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <BarChart2 size={14} />
            Análise
          </button>
        )}
      </div>

      {/* Aba Análise */}
      {abaAtiva === 'analise' && isAnalistaUp && (
        <AbaAnalise isAnalistaUp={isAnalistaUp} />
      )}

      {/* Conteúdo aba Inadimplentes */}
      {abaAtiva === 'lista' && <>

      {/* KPI Cards */}
      {loadingResumo ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
          &nbsp; Carregando KPIs...
        </div>
      ) : resumo ? (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <KpiCard
              title="Total em Aberto"
              value={formatMoeda(resumo.total_valor)}
              color="#e74c3c"
            />
            <KpiCard
              title="Inadimplentes (RFs)"
              value={resumo.total_rfs}
              color="#e67e22"
            />
            <div style={{
              background: '#fff',
              borderRadius: 10,
              padding: '1rem 1.25rem',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #3498db',
              flex: 1,
              minWidth: 160,
            }}>
              <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginBottom: 4 }}>Situação do Plano</div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div>
                  <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#27ae60' }}>{resumo.total_ativos}</span>
                  <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: 4 }}>Ativos</span>
                </div>
                <div>
                  <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#e74c3c' }}>{resumo.total_inativos}</span>
                  <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: 4 }}>Inativos</span>
                </div>
              </div>
            </div>
            <KpiCard
              title="Maior Atraso"
              value={`${resumo.maior_atraso} dias`}
              color="#9b59b6"
            />
            {resumo.lotes && resumo.lotes.length > 0 && (
              <div style={{
                background: '#fff',
                borderRadius: 10,
                padding: '1rem 1.25rem',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                borderLeft: '4px solid var(--brand, #20a490)',
                flex: 1,
                minWidth: 160,
              }}>
                <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginBottom: 6 }}>Lotes</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {resumo.lotes.map((lote) => (
                    <span key={lote} style={{
                      background: 'var(--brand, #20a490)22',
                      color: 'var(--brand, #20a490)',
                      borderRadius: 4,
                      padding: '0.1rem 0.45rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}>
                      {lote}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Faixas de atraso */}
          <FaixaBar
            porFaixa={resumo.por_faixa || []}
            faixaAtiva={faixaAtiva}
            onFaixaClick={handleFaixaClick}
          />
        </>
      ) : null}

      {/* Filtros */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        padding: '0.85rem 1.25rem',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <form onSubmit={handleBuscaSubmit} style={{ display: 'flex', gap: 6, flex: 2, minWidth: 200 }}>
          <input
            value={buscaInput}
            onChange={(e) => setBuscaInput(e.target.value)}
            placeholder="Buscar por nome ou CPF..."
            style={{
              flex: 1,
              padding: '0.45rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 6,
              fontSize: '0.85rem',
              outline: 'none',
            }}
          />
          <button type="submit" style={{
            background: 'var(--brand, #20a490)',
            color: 'var(--brand-ink, #fff)',
            border: 'none',
            borderRadius: 6,
            padding: '0.45rem 0.9rem',
            fontSize: '0.82rem',
            cursor: 'pointer',
          }}>
            Buscar
          </button>
        </form>

        <select
          value={snAtivo}
          onChange={(e) => { setSnAtivo(e.target.value); setPage(1); }}
          style={{ padding: '0.45rem 0.6rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.82rem' }}
        >
          <option value="">Todos (Ativo/Inativo)</option>
          <option value="S">Ativos</option>
          <option value="N">Inativos</option>
        </select>

        <select
          value={tpCobranca}
          onChange={(e) => { setTpCobranca(e.target.value); setPage(1); }}
          style={{ padding: '0.45rem 0.6rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.82rem' }}
        >
          <option value="">Todos (Boleto/Carnê)</option>
          <option value="B">Boleto</option>
          <option value="C">Carnê</option>
        </select>

        <select
          value={statusFiltro}
          onChange={(e) => { setStatusFiltro(e.target.value); setPage(1); }}
          style={{ padding: '0.45rem 0.6rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.82rem' }}
        >
          <option value="">Todos os status</option>
          <option value="A">Aberto</option>
          <option value="P">Parcial</option>
        </select>

        {(busca || faixaAtiva || snAtivo || statusFiltro || tpCobranca) && (
          <button
            onClick={handleLimparFiltros}
            style={{
              background: 'none',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '0.45rem 0.75rem',
              fontSize: '0.8rem',
              cursor: 'pointer',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <X size={12} />
            Limpar
          </button>
        )}
      </div>

      {/* Lista */}
      {loadingLista ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#aaa' }}>
          <RefreshCw size={22} />
          <div style={{ marginTop: 8 }}>Carregando inadimplentes...</div>
        </div>
      ) : lista.length === 0 ? (
        <div style={{
          background: '#fff',
          borderRadius: 10,
          padding: '3rem',
          textAlign: 'center',
          color: '#aaa',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <AlertCircle size={40} style={{ marginBottom: 8, opacity: 0.3 }} />
          <div>Nenhum inadimplente encontrado com os filtros aplicados.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
            Mostrando {inicioReg}–{fimReg} de {total} inadimplentes
            {faixaAtiva && (
              <span style={{ marginLeft: 8, background: '#f39c1222', color: '#e67e22', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.75rem' }}>
                Faixa: {faixaAtiva} dias
              </span>
            )}
          </div>

          <div>
            {lista.map((rf) => (
              <RFRow key={rf.mat_resp_fin} rf={rf} onAtendimento={handleAtendimento} onEnviarEmail={handleEnviarEmail} />
            ))}
          </div>

          {/* Paginacao */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              marginTop: '1.25rem',
              padding: '0.75rem',
            }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  background: page === 1 ? '#f5f5f5' : 'var(--brand, #20a490)',
                  color: page === 1 ? '#aaa' : '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '0.5rem 1.1rem',
                  cursor: page === 1 ? 'default' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', color: '#555' }}>
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  background: page === totalPages ? '#f5f5f5' : 'var(--brand, #20a490)',
                  color: page === totalPages ? '#aaa' : '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '0.5rem 1.1rem',
                  cursor: page === totalPages ? 'default' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                Próximo
              </button>
            </div>
          )}
        </>
      )}

      </>}

      {/* Modal Envio de Email */}
      {emailModalRf && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '24px', width: '460px',
            maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#1a1a2e' }}>
                <Mail size={18} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
                Enviar Notificação
              </h3>
              <button
                onClick={() => setEmailModalRf(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{
              background: '#f8f9fa', borderRadius: 8, padding: '12px', marginBottom: '16px',
              fontSize: '13px', color: '#555',
            }}>
              <strong>{emailModalRf.nome}</strong>
              <br />
              CPF: {formatCPF(emailModalRf.cpf)} | Mat: {emailModalRf.mat_resp_fin}
              <br />
              Email: {emailModalRf.email || <span style={{ color: '#e74c3c' }}>Sem email cadastrado</span>}
              <br />
              Atraso: <strong style={{ color: atrasoColor(emailModalRf.maior_atraso_mensalidade) }}>
                {emailModalRf.maior_atraso_mensalidade} dias
              </strong> | Total: <strong style={{ color: '#c0392b' }}>{formatMoeda(emailModalRf.total_valor)}</strong>
            </div>

            {emailLoadingTemplates ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
              </div>
            ) : emailTemplates.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px',
              }}>
                Nenhum template ativo.{' '}
                <a href="/admin/templates-email" style={{ color: 'var(--brand, #20a490)' }}>Criar template</a>
              </div>
            ) : (
              <>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>
                  Selecione o template:
                </label>
                <select
                  value={emailTemplateId}
                  onChange={(e) => setEmailTemplateId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                    borderRadius: '6px', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box',
                  }}
                >
                  <option value="">-- Selecione --</option>
                  {emailTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome} {t.regra_dias_atraso ? `(>${t.regra_dias_atraso}d)` : ''}
                    </option>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setEmailModalRf(null)}
                    style={{
                      padding: '10px 18px', background: '#f5f5f5', border: '1px solid #ddd',
                      borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmarEnvio}
                    disabled={!emailTemplateId || emailEnviando || !emailModalRf.email}
                    style={{
                      padding: '10px 18px', background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 600,
                      opacity: (!emailTemplateId || emailEnviando || !emailModalRf.email) ? 0.5 : 1,
                    }}
                  >
                    {emailEnviando ? 'Enviando...' : 'Enviar Notificação'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DashboardInadimplencia;
